
import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { MessageSquare, Users, Phone, Mail } from 'lucide-react';

interface ContactFeatureProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const ContactFeature: React.FC<ContactFeatureProps> = ({ icon, title, description }) => {
  return (
    <div className="flex flex-col items-center md:items-start space-y-2 md:space-y-3 p-3 md:p-4 hover:bg-purple-900/20 rounded-lg transition-colors">
      <div className="bg-purple-600/30 p-3 rounded-full">
        {icon}
      </div>
      <h3 className="font-semibold text-white text-base md:text-lg">
        {title}
      </h3>
      <p className="text-gray-300 text-xs md:text-sm">
        {description}
      </p>
    </div>
  );
};

const ContactFeatureGrid: React.FC = () => {
  const { currentLanguage } = useLanguage();
  
  // Content based on selected language
  const features = [
    {
      icon: <MessageSquare className="w-5 h-5 md:w-6 md:h-6 text-purple-300" aria-hidden="true" />,
      title: currentLanguage === 'de' ? "Persönliche Beratung" :
             currentLanguage === 'en' ? "Personal Consultation" :
             currentLanguage === 'fr' ? "Consultation Personnelle" :
             "Consulenza Personale",
      description: currentLanguage === 'de' ? "Individuelles Strategiegespräch für Ihre Social-Media-Kampagne" :
                  currentLanguage === 'en' ? "Individual strategy meeting for your social media campaign" :
                  currentLanguage === 'fr' ? "Réunion de stratégie individuelle pour votre campagne sur les médias sociaux" :
                  "Incontro strategico individuale per la tua campagna sui social media"
    },
    {
      icon: <Users className="w-5 h-5 md:w-6 md:h-6 text-purple-300" aria-hidden="true" />,
      title: currentLanguage === 'de' ? "Zielgruppenfokus" :
             currentLanguage === 'en' ? "Target Audience Focus" :
             currentLanguage === 'fr' ? "Focus sur le Public Cible" :
             "Focus sul Pubblico Target",
      description: currentLanguage === 'de' ? "KI-optimierte Videos speziell für Ihre Schweizer Zielgruppe" :
                  currentLanguage === 'en' ? "AI-optimized videos specifically for your Swiss target audience" :
                  currentLanguage === 'fr' ? "Vidéos optimisées par l'IA spécifiquement pour votre public cible suisse" :
                  "Video ottimizzati con IA specificamente per il tuo pubblico target svizzero"
    },
    {
      icon: <Phone className="w-5 h-5 md:w-6 md:h-6 text-purple-300" aria-hidden="true" />,
      title: currentLanguage === 'de' ? "24h Support" :
             currentLanguage === 'en' ? "24h Support" :
             currentLanguage === 'fr' ? "Support 24h" :
             "Supporto 24h",
      description: currentLanguage === 'de' ? "Schnelle Antwort innerhalb eines Werktages für Ihre Video-Anfrage" :
                  currentLanguage === 'en' ? "Fast response within one business day for your video request" :
                  currentLanguage === 'fr' ? "Réponse rapide dans un jour ouvrable pour votre demande de vidéo" :
                  "Risposta rapida entro un giorno lavorativo per la tua richiesta di video"
    },
    {
      icon: <Mail className="w-5 h-5 md:w-6 md:h-6 text-purple-300" aria-hidden="true" />,
      title: currentLanguage === 'de' ? "Kostenlose Analyse" :
             currentLanguage === 'en' ? "Free Analysis" :
             currentLanguage === 'fr' ? "Analyse Gratuite" :
             "Analisi Gratuita",
      description: currentLanguage === 'de' ? "Gratis Social-Media-Marketing-Analyse für Ihren Brand" :
                  currentLanguage === 'en' ? "Free social media marketing analysis for your brand" :
                  currentLanguage === 'fr' ? "Analyse gratuite du marketing des médias sociaux pour votre marque" :
                  "Analisi gratuita del marketing sui social media per il tuo marchio"
    }
  ];
  
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 text-left">
      {features.map((feature, index) => (
        <ContactFeature 
          key={index}
          icon={feature.icon}
          title={feature.title}
          description={feature.description}
        />
      ))}
    </div>
  );
};

export default ContactFeatureGrid;
